import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSharedState } from "state";
import Select from "react-select";
import { IconPlus, IconX, IconPencil } from "@tabler/icons-react";
import { v4 as uuidv4 } from "uuid";
import ModalComponent from "components/modal";
import { createFormula, updateFormula } from "api/budget_request";

// Define types for formula parts
interface FormulaPart {
  id: string;
  type: "operator" | "level" | "number" | "formula";
  value: string | number; // For levels, this is level.id. For operators, the operator string. For numbers, the number. For formulas, the formula id.
  display: string; // Text to display (level name, operator, number as string, formula name)
}

const OPERATORS = ["(", ")", "+", "-", "*", "/"] as const;

// Options for the main add dropdown
const ADD_OPTIONS = [
  { value: "operator", label: "Символ (оператор)" },
  { value: "level", label: "Уровень бюджета" },
  { value: "number", label: "Число" },
  { value: "formula", label: "Формула" },
] as const;
type AddOptionType = (typeof ADD_OPTIONS)[number]["value"];

type SelectOption = {
  value: number;
  label: string;
  disabled?: boolean;
};

export default function NewFormula() {
  const { getFormulasData, formulas, levels, getLevelsData, notify } = useSharedState();
  const { formulaId } = useParams();
  const navigate = useNavigate();

  const [formulaName, setFormulaName] = useState<string>("");
  // State for the formula itself
  const [formulaParts, setFormulaParts] = useState<FormulaPart[]>([]);
  const [currentBudget, setCurrentBudget] = useState<number>(0);
  const [budgets, setBudgets] = useState<SelectOption[]>([]);
  const [budgetLevels, setBudgetLevels] = useState<SelectOption[][]>([]);
  const [vatBudget, setVatBudget] = useState<(boolean | null)[]>([]);
  const VAT_OPTIONS = [
    { value: null, label: "Не фильтровать" },
    { value: true, label: "С НДС" },
    { value: false, label: "Без НДС" },
  ];
  // State for the "add part" UI
  const [currentAddingType, setCurrentAddingType] = useState<AddOptionType | null>(null);
  const [inputValue, setInputValue] = useState<string>(""); // For number input
  const [isFormLoading, setFormLoading] = useState<boolean>(false);
  const [isFormSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [editingPart, setEditingPart] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  // Load levels and formulas if needed
  useEffect(() => {
    getLevelsData();
    getFormulasData();
  }, []);

  // Editing logic: load formula if formulaId is present
  useEffect(() => {
    if (formulaId && formulas.length > 0) {
      const formula = formulas.find((f) => f.budget_formula_id === Number(formulaId));
      if (!formula) {
        notify("Формула не найдена", "danger");
        navigate("/formulas");
        return;
      }
      setIsEditMode(true);
      setFormulaName(formula.formula_name);
      // Reconstruct formulaParts from expression_list and terms
      // We'll try to match level parts by name in terms
      const parts: FormulaPart[] = formula.expression_list.map((display, idx) => {
        // Try to find if this display is a level (term)
        const termIdx = formula.terms.findIndex((t) => t.name === display);
        if (termIdx !== -1) {
          // Level part
          return {
            id: uuidv4(),
            type: "level",
            value: formula.terms[termIdx].filters.lvl_0[0], // main budget id
            display,
          };
        }
        // Try to parse as number
        if (!isNaN(Number(display))) {
          return {
            id: uuidv4(),
            type: "number",
            value: Number(display),
            display,
          };
        }
        // Otherwise, treat as operator
        return {
          id: uuidv4(),
          type: "operator",
          value: display,
          display,
        };
      });
      setFormulaParts(parts);
      // Fill budgets, budgetLevels, vatBudget for level parts
      const newBudgets: SelectOption[] = [];
      const newBudgetLevels: SelectOption[][] = [];
      const newVatBudget: (boolean | null)[] = [];
      formula.terms.forEach((term) => {
        // lvl_0 is main budget
        const mainBudgetId = term.filters.lvl_0[0];
        const mainBudget = levels.find((lvl) => lvl.budget_type_id === mainBudgetId);
        newBudgets.push(
          mainBudget
            ? { value: mainBudgetId, label: mainBudget.budget_type!, disabled: !!!mainBudget.status }
            : { value: mainBudgetId, label: String(mainBudgetId) }
        );
        // All other lvls
        const levelsArr: SelectOption[] = [];
        Object.entries(term.filters).forEach(([key, arr]) => {
          if (key !== "lvl_0" && arr.length > 0) {
            const lvl = levels.find((l) => l.budget_type_id === arr[0]);
            if (lvl) levelsArr.push({ value: lvl.budget_type_id!, label: lvl.budget_type!, disabled: !!!lvl.status });
          }
        });
        newBudgetLevels.push(levelsArr);
        // VAT (optional)
        newVatBudget.push((term as any).vat ?? null);
      });
      setBudgets(newBudgets);
      setBudgetLevels(newBudgetLevels);
      setVatBudget(newVatBudget);
    } else {
      setIsEditMode(false);
    }
  }, [formulaId, formulas, levels]);

  const currentBudgetMaxLevels = useMemo(() => {
    return levels.find((lvl) => lvl.budget_type_id === budgets[currentBudget - 1]?.value)?.max_levels || 0;
  }, [levels, currentBudget, budgets]);

  const formulaBudgets: SelectOption[] = useMemo(() => {
    return formulaParts
      .filter((part) => part.type === "level")
      .map((part) => ({
        value: +part.value,
        label: part.display,
      }));
  }, [formulaParts]);

  const levelOptions: SelectOption[] = useMemo(() => {
    return levels
      .filter((level) => level.parent_budget_type_id === null)
      .map((level) => ({
        value: level.budget_type_id!,
        label: level.budget_type!,
        disabled: !!!level.status,
      }));
  }, [levels]);

  const operatorOptions = useMemo(() => {
    return OPERATORS.map((op) => ({ value: op, label: op }));
  }, []);

  const cancelAddPart = () => {
    setCurrentAddingType(null);
    setInputValue("");
    setEditingPart(null);
  };

  const handleAddPart = (part: Omit<FormulaPart, "id">) => {
    if (editingPart) {
      setFormulaParts((prevParts) => prevParts.map((p) => (p.id === editingPart ? { ...part, id: p.id } : p)));
      setEditingPart(null);
    } else {
      setFormulaParts((prevParts) => [...prevParts, { ...part, id: uuidv4() }]);
    }
    cancelAddPart();
  };

  const handleRemovePart = (id: string) => {
    const deletedPart = formulaParts.find((part) => part.id === id);
    const deletedPartIndex = formulaParts.indexOf(deletedPart!);
    if (deletedPart?.type === "level") {
      setBudgets((prev) => prev.filter((_, budgetIndex) => budgetIndex !== deletedPartIndex));
      setBudgetLevels((prev) => prev.filter((_, levelIndex) => levelIndex !== deletedPartIndex));
      setCurrentBudget(currentBudget - 1);
    }
    setFormulaParts((prevParts) => prevParts.filter((part) => part.id !== id));
  };

  const handleEditPart = (id: string) => {
    const part = formulaParts.find((p) => p.id === id);
    if (!part) return;

    if (part.type === "level") {
      setCurrentAddingType("level");
      const budgetIndex = [...formulaParts].filter((p) => p.type === "level").findIndex((p) => p.id === id);
      setCurrentBudget(budgetIndex + 1);
    } else if (part.type === "operator") {
      setCurrentAddingType("operator");
    } else if (part.type === "number") {
      setCurrentAddingType("number");
      setInputValue(part.value.toString());
    }
    setEditingPart(id);
  };

  const handleAddTypeSelected = (type: AddOptionType) => {
    if (type === "level") {
      setCurrentBudget(budgets.length + 1);
    }
    setCurrentAddingType(type);
  };

  const clearBudget = () => {
    if (editingPart) {
      setCurrentBudget(budgets.length + 1);
    } else {
      setBudgets((prev) => prev.slice(0, currentBudget - 1));
      setBudgetLevels((prev) => prev.slice(0, currentBudget - 1));
      setVatBudget((prev) => prev.slice(0, currentBudget - 1));
      setCurrentBudget(currentBudget - 1);
    }
    cancelAddPart();
  };

  const saveBudget = () => {
    if (budgetLevels.length && budgetLevels[currentBudget - 1].length) {
      handleAddPart({
        type: "level",
        value: budgetLevels[currentBudget - 1][budgetLevels[currentBudget - 1].length - 1].value,
        display: budgetLevels[currentBudget - 1][budgetLevels[currentBudget - 1].length - 1].label,
      });
    } else {
      handleAddPart({
        type: "level",
        value: budgets[currentBudget - 1].value,
        display: budgets[currentBudget - 1].label,
      });
    }
  };

  const clearFormula = () => {
    setFormulaParts([]);
    setBudgets([]);
    setBudgetLevels([]);
    setVatBudget([]);
    setCurrentBudget(0);
    cancelAddPart();
  };

  const saveFormula = async () => {
    setFormLoading(true);
    try {
      const payload = {
        formula_name: formulaName,
        expression_list: formulaParts.map((part) => part.display),
        terms: formulaParts
          .filter((part) => part.type === "level" || part.type === "formula")
          .map((part, index) => {
            if (part.type === "formula") {
              return {
                name: part.display,
                vat: null,
                filters: null,
                reference_budget_formula_id: part.value,
              };
            }
            return {
              name: part.display,
              vat: vatBudget[index],
              filters: {
                lvl_0: [budgets[index].value],
                ...Object.fromEntries(budgetLevels[index].map((lvl, lvlIndex) => [`lvl_${lvlIndex + 1}`, [lvl.value]])),
              },
            };
          }),
      };
      if (isEditMode && formulaId) {
        await updateFormula(Number(formulaId), payload);
        notify("Формула обновлена", "success");
      } else {
        await createFormula(payload);
        notify("Формула сохранена", "success");
      }
      setFormSubmitted(true);
      await getFormulasData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", isEditMode ? "Ошибка при обновлении формулы" : "Ошибка при сохранении формулы");
    } finally {
      setFormLoading(false);
    }
  };

  // Component to render the current formula with edit and delete buttons
  const FormulaDisplay = () => (
    <div className="d-flex flex-column gap-1 mt-3">
      <label className="form-label fw-bold">Текущая формула:</label>
      <div className="d-flex align-items-center gap-2 p-3 border rounded-2xl bg-light">
        {formulaParts.length === 0 && <span className="text-muted">Формула пуста</span>}
        {formulaParts.map((part) => (
          <span key={part.id} className="badge bg-secondary d-flex align-items-center fs-6">
            {part.display}
            <div className="d-flex align-items-center ms-2">
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-white"
                aria-label="Редактировать элемент"
                title="Редактировать элемент"
                onClick={() => handleEditPart(part.id)}
                onMouseEnter={(e) => e.currentTarget.classList.add("text-secondary")}
                onMouseLeave={(e) => e.currentTarget.classList.remove("text-secondary")}
              >
                <IconPencil size={16} stroke={2} />
              </button>
              <button
                type="button"
                className="btn btn-link btn-sm p-0 ms-1 text-white"
                aria-label="Удалить элемент"
                title="Удалить элемент"
                onClick={() => handleRemovePart(part.id)}
                onMouseEnter={(e) => e.currentTarget.classList.add("text-secondary")}
                onMouseLeave={(e) => e.currentTarget.classList.remove("text-secondary")}
              >
                <IconX size={16} stroke={2} />
              </button>
            </div>
          </span>
        ))}
      </div>
    </div>
  );

  // Component for adding new parts
  const AddPartComponent = () => {
    if (currentAddingType === "level") {
      return (
        <div className="mb-3 p-3 border rounded-2xl">
          <label className="form-label fw-bold">Бюджет:</label>
          <div className="d-flex align-items-center gap-2 mb-2">
            <Select
              options={levelOptions}
              value={budgets[currentBudget - 1]}
              onChange={(selectedOption) => {
                if (selectedOption) {
                  setBudgets((prev) => {
                    const newBudgets = [...prev];
                    newBudgets[currentBudget - 1] = selectedOption;
                    return newBudgets;
                  });
                  setBudgetLevels((prev) => {
                    const newLevels = [...prev];
                    if (newLevels.length >= currentBudget) {
                      newLevels[currentBudget - 1] = [];
                      return newLevels;
                    } else {
                      newLevels.push([]);
                      return newLevels;
                    }
                  });
                }
              }}
              isOptionDisabled={(option) => option.disabled || false}
              placeholder="Выберите бюджет"
              classNamePrefix="react-select"
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />

            {Array.from({ length: currentBudgetMaxLevels }, (_, index) => index + 1).map(
              (currLvl, index) =>
                (currLvl === 1 || (currLvl > 1 && budgetLevels[currentBudget - 1][index - 1])) && (
                  <Select
                    key={index}
                    options={levels
                      .filter(
                        (lvl) =>
                          lvl.parent_budget_type_id === budgets[currentBudget - 1]?.value &&
                          lvl.level_number === currLvl
                      )
                      .map((lvl) => ({
                        value: lvl.budget_type_id!,
                        label: lvl.budget_type!,
                        disabled: !!!lvl.status,
                      }))}
                    value={
                      budgetLevels.length && budgetLevels[currentBudget - 1]?.length
                        ? budgetLevels[currentBudget - 1][index]
                        : null
                    }
                    onChange={(selectedOption) => {
                      if (selectedOption) {
                        setBudgetLevels((prev) => {
                          if (!prev.length) {
                            return [[selectedOption]];
                          }
                          const newBudgetLevels = [...prev];
                          if (!newBudgetLevels[currentBudget - 1]) {
                            newBudgetLevels[currentBudget - 1] = [];
                          }
                          newBudgetLevels[currentBudget - 1][index] = selectedOption;
                          return newBudgetLevels;
                        });
                      } else {
                        setBudgetLevels((prev) => {
                          const newBudgetLevels = [...prev];
                          newBudgetLevels[currentBudget - 1] = newBudgetLevels[currentBudget - 1].slice(0, index);
                          return newBudgetLevels;
                        });
                      }
                    }}
                    isClearable
                    isOptionDisabled={(option) => option.disabled || false}
                    placeholder={`Выберите уровень ${currLvl}`}
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                )
            )}
          </div>

          <label className="form-label fw-bold header-required">Фильтр по НДС:</label>
          <div className="d-flex align-items-center gap-2 mb-3">
            <Select
              options={VAT_OPTIONS}
              value={
                vatBudget[currentBudget - 1] !== undefined
                  ? {
                      value: vatBudget[currentBudget - 1],
                      label:
                        vatBudget[currentBudget - 1] === null
                          ? VAT_OPTIONS[0].label
                          : vatBudget[currentBudget - 1]
                          ? VAT_OPTIONS[1].label
                          : VAT_OPTIONS[2].label,
                    }
                  : undefined
              }
              onChange={(selectedOption) => {
                setVatBudget((prev) => {
                  const newVatBudget = [...prev];
                  newVatBudget[currentBudget - 1] = selectedOption?.value!;
                  return newVatBudget;
                });
              }}
              placeholder="Выберите значение"
              classNamePrefix="react-select"
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>

          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={clearBudget}
              disabled={isFormLoading}
            >
              Отмена
            </button>

            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={saveBudget}
              disabled={isFormLoading || !!!budgets[currentBudget - 1] || vatBudget[currentBudget - 1] === undefined}
            >
              Подтвердить
            </button>
          </div>
        </div>
      );
    }

    if (currentAddingType === "operator") {
      return (
        <div className="mb-3 p-3 border rounded-2xl">
          <label className="form-label fw-bold">Выберите символ:</label>
          <Select
            options={operatorOptions}
            onChange={(selectedOption) => {
              if (selectedOption) {
                handleAddPart({
                  type: "operator",
                  value: selectedOption.value,
                  display: selectedOption.label,
                });
              }
            }}
            placeholder="Выберите символ..."
            classNamePrefix="react-select"
            autoFocus
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary mt-2"
            onClick={() => setCurrentAddingType(null)}
          >
            Отмена
          </button>
        </div>
      );
    }

    if (currentAddingType === "number") {
      return (
        <div className="mb-3 p-3 border rounded-2xl">
          <label htmlFor="numberInput" className="form-label fw-bold">
            Введите число:
          </label>
          <div className="input-group">
            <input
              id="numberInput"
              type="number"
              className="form-control rounded-l-2xl"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Например, 12 или 0,5"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const num = parseFloat(inputValue);
                  if (!isNaN(num)) {
                    handleAddPart({ type: "number", value: num, display: inputValue });
                  } else if (notify) {
                    notify("Пожалуйста, введите корректное число", "danger");
                  }
                }
              }}
            />
            <button
              className="btn btn-outline-primary"
              type="button"
              onClick={() => {
                const num = parseFloat(inputValue);
                if (!isNaN(num)) {
                  handleAddPart({ type: "number", value: num, display: inputValue });
                } else if (notify) {
                  notify("Пожалуйста, введите корректное число", "danger");
                }
              }}
            >
              Добавить
            </button>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary mt-2"
            onClick={() => setCurrentAddingType(null)}
          >
            Отмена
          </button>
        </div>
      );
    }

    if (currentAddingType === "formula") {
      return (
        <div className="mb-3 p-3 border rounded-2xl">
          <label className="form-label fw-bold">Выберите формулу:</label>
          <Select
            options={formulas?.map((formula) => ({
              value: formula.budget_formula_id,
              label: formula.formula_name,
            }))}
            onChange={(selectedOption) => {
              if (selectedOption) {
                handleAddPart({
                  type: "formula",
                  value: selectedOption.value,
                  display: selectedOption.label,
                });
              }
            }}
            placeholder="Выберите формулу..."
            classNamePrefix="react-select"
            autoFocus
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary mt-2"
            onClick={() => setCurrentAddingType(null)}
          >
            Отмена
          </button>
        </div>
      );
    }

    // Show the main "Add Part Type" dropdown button
    return (
      <div className="dropdown mb-3">
        <button
          className="btn btn-primary dropdown-toggle"
          type="button"
          id="addPartDropdownToggle"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          <IconPlus size={18} className="me-1" /> Добавить элемент формулы
        </button>
        <ul className="dropdown-menu" aria-labelledby="addPartDropdownToggle">
          {ADD_OPTIONS.map((opt) => (
            <li key={opt.value}>
              <button className="dropdown-item" type="button" onClick={() => handleAddTypeSelected(opt.value)}>
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const newFormulaBody = (
    <>
      <div className="mb-3">
        <label className="form-label fw-bold">Название формулы</label>
        <div className="d-flex align-items-center gap-2">
          <input
            type="text"
            className="form-control rounded-2xl"
            placeholder="Введите название формулы"
            value={formulaName}
            onChange={(e) => setFormulaName(e.target.value)}
            autoFocus
          />
        </div>
        <FormulaDisplay />
      </div>
      <AddPartComponent />
    </>
  );

  const newFormulaFooter = formulaName && formulaParts.length > 0 && (
    <>
      <button type="button" className="btn btn-ghost-danger me-2" onClick={clearFormula} disabled={isFormLoading}>
        Очистить формулу
      </button>
      <button type="button" className="btn btn-primary me-2" onClick={saveFormula} disabled={isFormLoading}>
        Сохранить
      </button>
    </>
  );

  return (
    <ModalComponent
      header={"Конструктор формул"}
      body={newFormulaBody}
      footer={newFormulaFooter}
      disableClose={isFormLoading}
      shouldModalClose={isFormSubmitted}
    />
  );
}
